import {
  Divider,
  Flex,
  Group,
  Table as MantineTable,
  NumberInput,
  ScrollArea,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useSyncExternalStore,
} from 'react';

import { useTranslation } from 'react-i18next';
import type { Spending } from '../../../api/financials-client/financials-client.type';
import type { Project } from '../../../api/projects-client/projects-client.type';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import { useFinancialsStore } from '../../../store/financials-store/financials-store';
import { setTableSearchTerm } from '../../../store/financials-store/financials-store.actions';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { baseWhite, inkLight, skyDark } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Icon } from '../../../ui-library/icon/icon';
import { toast } from '../../notifications';
import { usePortfolioProjectsAndBoards, useSaveSpendingMutation } from '../financials.hooks';
import { buildSpendingPayload, filterProjectsBySearch } from './by-date.helpers';
import { SpendingFormProvider, useSpendingForm, useSpendingFormContext } from './by-date.context';
import { RollupStore, RollupStoreProvider, rollupKey, useRollupStore } from './by-date.rollup-store';
import type { ByDateProps, ProjectRollupCellProps, ProjectRowProps, SpendingForm } from './by-date.type';

dayjs.extend(localeData);

// Hoisted from inline `dayjs().localeData().months()` per render of each project row.
// Locale data is static for a given app session; computing once on module load saves
// O(projects × 2) dayjs calls per render in the previous implementation.
const MONTH_NAMES: readonly string[] = Object.freeze(dayjs().localeData().months());
const MONTH_LABELS: readonly string[] = Object.freeze(dayjs().localeData().monthsShort());

/**
 * Builds the cartesian product of `projects × subprojects × 12 months` and overlays
 * persisted `spendingData` on top. Every cell rendered in the editor exists as a
 * form row from the moment the form is initialized, which lets every NumberInput
 * use `form.getInputProps(...)` instead of branching between "blank-input" and
 * "existing-row" code paths. That branch was the source of the
 * `form.insertListItem`-during-typing race that broke uncontrolled mode.
 *
 * @param {Project[] | undefined} projects - Portfolio projects (with subprojects)
 * @param {Spending[] | undefined} spendingData - Persisted spending rows
 * @param {number | null} selectedYear - The active year (rows are scoped to this year)
 * @param {string} portfolioId - Canonical portfolio id stamped onto every pre-populated cell
 *   so first-time saves produce a valid UUID payload even when no persisted spending exists
 *   to copy a `portfolio_id` from
 * @returns {SpendingForm[]} All cells for the year, persisted values where present, `budget: null` otherwise
 */
function buildInitialExpenditures(
  projects: Project[] | undefined,
  spendingData: Spending[] | undefined,
  selectedYear: number | null,
  portfolioId: string,
): SpendingForm[] {
  if (!projects || !selectedYear) return [];

  const persistedByKey = new Map<string, Spending>();
  for (const entry of spendingData ?? []) {
    if (entry.year !== selectedYear) continue;
    if (!entry.subproject_id) continue;
    persistedByKey.set(`${entry.project_id}-${entry.subproject_id}-${entry.year}-${entry.month}`, entry);
  }

  const rows: SpendingForm[] = [];
  for (const project of projects) {
    for (const subproject of project.subprojects) {
      for (let month = 1; month <= 12; month += 1) {
        const key = `${project.id}-${subproject.id}-${selectedYear}-${month}`;
        const persisted = persistedByKey.get(key);
        rows.push({
          key,
          project_id: project.id,
          subproject_id: subproject.id,
          portfolio_id: persisted?.portfolio_id ?? portfolioId,
          year: selectedYear,
          month,
          budget: persisted?.budget ?? null,
          valid: persisted?.valid ?? true,
          autofilled: persisted?.autofilled ?? false,
        });
      }
    }
  }
  return rows;
}

/**
 * Content-aware fingerprint of `spendingData` for the year being edited.
 * Identical-payload refetches via React Query structural sharing produce the
 * same string and skip the reset effect, preserving in-progress edits.
 *
 * Mirrors every persisted field that `buildInitialExpenditures` hydrates onto
 * each `SpendingForm` row (`budget`, `portfolio_id`, `valid`, `autofilled`) so
 * a refetch that flips one of those server-managed flags actually re-seeds the
 * form instead of silently keeping the stale value.
 *
 * @param {Spending[] | undefined} spendingData - Persisted rows
 * @param {number | null} selectedYear - The active year
 * @returns {string} A delimited fingerprint
 */
function computeSpendingFingerprint(spendingData: Spending[] | undefined, selectedYear: number | null): string {
  if (!spendingData || !selectedYear) return '';
  const parts: string[] = [];
  for (const entry of spendingData) {
    if (entry.year !== selectedYear) continue;
    parts.push(
      `${entry.project_id}|${entry.subproject_id ?? ''}|${entry.month}|${entry.budget ?? ''}|${entry.portfolio_id ?? ''}|${entry.valid ?? ''}|${entry.autofilled ?? ''}`,
    );
  }
  parts.sort();
  return parts.join('§');
}

/**
 * "Edit Financial Data → By Date" tab. Renders a monthly budget grid grouped
 * by project / subproject. Persists on Save Changes; no per-keystroke API calls.
 *
 * @param {ByDateProps} props - Container-driven props
 * @returns {JSX.Element} The By Date editor
 */
function ByDate({ spendingData, isLoading }: ByDateProps) {
  const { t } = useTranslation();
  const portfolio = useGlobalStore((s) => s.portfolio)!;
  const selectedYear = useFinancialsStore((s) => s.table.selectedYear);
  const searchTerm = useFinancialsStore((s) => s.table.searchTerm);
  const { data: projects } = usePortfolioProjectsAndBoards(portfolio.id);

  const saveMutation = useSaveSpendingMutation();

  const form = useSpendingForm({
    mode: 'uncontrolled',
    initialValues: {
      expenditures: buildInitialExpenditures(projects, spendingData, selectedYear, portfolio.id),
    },
  });

  // Include `projects` structure and `selectedYear` directly so the reset effect
  // re-seeds the form when those load asynchronously after mount. Without this,
  // `computeSpendingFingerprint(spendingData, selectedYear)` returns `''` both
  // when `selectedYear` is `null` AND when there are no spending entries for the
  // selected year — meaning `previousFingerprintRef.current` (captured as `''`
  // at mount) compares equal to the post-load fingerprint, the guard bails, and
  // the form stays at `expenditures: []`. The bug manifests on portfolios with
  // no persisted spending data: every subproject cell short-circuits to an
  // empty `<Td/>` because `expenditureIndexByKey` is empty.
  const dataFingerprint = useMemo(() => {
    const projectsFp = (projects ?? []).map((p) => `${p.id}:${p.subprojects.map((s) => s.id).join(',')}`).join('|');
    const spendingFp = computeSpendingFingerprint(spendingData, selectedYear);
    return `${projectsFp}§${selectedYear ?? ''}§${spendingFp}`;
  }, [projects, selectedYear, spendingData]);

  // Reset effect runs only when the persisted snapshot's CONTENT changes (not just its
  // reference). Dirty guard prevents background refetches from clobbering in-progress
  // typing. See https://react.dev/learn/you-might-not-need-an-effect for the pattern.
  //
  // First-run guard: on initial mount the effect would otherwise run `setValues` with
  // content bit-identical to `useForm({ initialValues })`. In uncontrolled mode that
  // bumps `formKey` (per Mantine 9 use-form.ts) and re-mounts every NumberInput
  // unnecessarily — defeating the perf win. We track the previous fingerprint in a ref
  // and skip the effect body when it hasn't changed.
  //
  // Why `setValues` + `resetDirty` instead of `initialize`: Mantine's `form.initialize`
  // is documented as a one-time-per-lifecycle call; subsequent calls are silently
  // ignored. `setValues` updates the current values AND (in uncontrolled mode) bumps
  // `form.key(...)` so that mounted NumberInputs re-render with the new defaultValue.
  // `resetDirty(values)` then re-baselines the dirty comparison to the new values so
  // the Save button correctly hides until the user makes a fresh edit.
  // Mantine 9's `form.resetDirty(...)` in uncontrolled mode mutates `dirtyRef`
  // but does NOT call `setDirtyState`, so it never schedules a re-render
  // (see @mantine/form/cjs/hooks/use-form-status/use-form-status.cjs: setDirty
  // skips setDirtyState unless mode==='controlled' OR forceUpdate=true).
  // After Save we therefore force a render so the JSX re-evaluates
  // `form.isDirty()` and hides the Save Changes button.
  const [, forceRender] = useReducer((tick: number) => tick + 1, 0);

  const previousFingerprintRef = useRef<string | null>(dataFingerprint);
  // Pending refs hold the most recent refetch deferred while the form was dirty.
  // Without these, a snapshot that arrives mid-edit is dropped permanently (the
  // effect bails once and `dataFingerprint` never changes back). On Cancel we
  // apply the pending snapshot; on Save we clear it because the post-save
  // invalidation will hydrate with a merged server snapshot anyway.
  const pendingFingerprintRef = useRef<string | null>(null);
  const pendingExpendituresRef = useRef<SpendingForm[] | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `form` literal changes every render but its methods are stable; depending on the literal would cause infinite loops (see use-date-sync.ts:11-16).
  useEffect(() => {
    if (previousFingerprintRef.current === dataFingerprint) return;
    const nextExpenditures = buildInitialExpenditures(projects, spendingData, selectedYear, portfolio.id);
    if (form.isDirty()) {
      pendingFingerprintRef.current = dataFingerprint;
      pendingExpendituresRef.current = nextExpenditures;
      return;
    }
    previousFingerprintRef.current = dataFingerprint;
    form.setValues({ expenditures: nextExpenditures });
    form.resetDirty({ expenditures: nextExpenditures });
  }, [dataFingerprint, projects, selectedYear]);

  // Applies a queued snapshot. Returns true when a pending snapshot was applied,
  // false when there was nothing to drain. Called by Cancel so the user reverts
  // to the latest server state (which supersedes both their edits AND the
  // pre-edit form values).
  // biome-ignore lint/correctness/useExhaustiveDependencies: `form` instability — see comment above
  const drainPendingSnapshot = useCallback((): boolean => {
    const pendingFp = pendingFingerprintRef.current;
    const pendingExp = pendingExpendituresRef.current;
    if (!pendingFp || !pendingExp) return false;
    pendingFingerprintRef.current = null;
    pendingExpendituresRef.current = null;
    previousFingerprintRef.current = pendingFp;
    form.setValues({ expenditures: pendingExp });
    form.resetDirty({ expenditures: pendingExp });
    return true;
  }, []);

  // Dedicated pub/sub store for monthly rollups. Replaces a per-cell `form.watch`
  // that was firing all ~432 subscribers synchronously inside `setFieldValue`
  // (measured: 187ms per keystroke before this change). With the store, only the
  // ONE cell whose rollup changes gets notified, and only THAT cell re-renders.
  const rollupStore = useMemo(() => new RollupStore(), []);

  // Recompute every slot from the form's current values. Called from:
  //   1. The bulk-populate layout effect below (mount + data refetch)
  //   2. The Cancel button (`form.reset()` reverts the form but does NOT emit
  //      watch notifications per Mantine `use-form-values.cjs`, so the store
  //      would otherwise stay stuck at the post-typing values)
  // biome-ignore lint/correctness/useExhaustiveDependencies: see note about `form` instability above.
  const rebuildRollups = useCallback(() => {
    if (!projects || !selectedYear) return;
    const values = form.getValues().expenditures;
    for (const project of projects) {
      for (let m = 1; m <= 12; m += 1) {
        rollupStore.set(
          rollupKey(project.id, selectedYear, m),
          computeProjectRollup(values, project.id, selectedYear, m),
        );
      }
    }
  }, [projects, selectedYear, rollupStore]);

  // Seed/refresh the store whenever the persisted snapshot changes.
  //
  // useLayoutEffect (not useEffect): runs synchronously after the DOM mutates
  // but before the browser paints. Prevents the first-paint flicker where every
  // ProjectRollupCell would briefly render '' (empty store) and then flash to
  // its correct rollup value once the effect fires.
  //
  // `dataFingerprint` is the load-bearing dep here: identical-payload React Query
  // refetches don't change `projects` / `selectedYear` / `rollupStore`, so without
  // it we'd miss content changes. Biome's static analyzer can't see this because
  // the dep flows through `form.getValues()` (a closure read).
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
  useLayoutEffect(() => {
    rebuildRollups();
  }, [dataFingerprint, rebuildRollups]);

  const filteredProjects = useMemo(
    () => (projects ? filterProjectsBySearch(projects, searchTerm ?? '') : []),
    [projects, searchTerm],
  );

  return (
    <SpendingFormProvider form={form}>
      <RollupStoreProvider value={rollupStore}>
        <BrandedLoadingOverlay visible={isLoading} variant="colored" />
        <Stack>
          <Group justify="space-between">
            <Group>
              <Text fw={700} size="lg">
                All Projects
              </Text>
            </Group>
            <Group>
              {form.isDirty() && (
                <Group>
                  <Button
                    variant="primary"
                    onClick={() => {
                      // Filter out cells that don't represent a value the user wants to
                      // persist:
                      // - `null` / `undefined`: pre-populated rows the user never touched
                      // - `''` (empty string): a cell the user cleared. `buildSpendingPayload`
                      //   at by-date.helpers.ts:232 would otherwise drop these silently via
                      //   its `!== ''` filter; explicit filter here keeps the boundary clear.
                      // Typed zeros (`budget: 0`) are preserved — `0` is a legitimate value.
                      const expenditures = form
                        .getValues()
                        .expenditures.filter(
                          (e) => e.budget !== null && e.budget !== undefined && (e.budget as unknown) !== '',
                        );
                      const payload = buildSpendingPayload(expenditures, selectedYear, portfolio.id);
                      if (payload) {
                        saveMutation.mutate(payload, {
                          onSuccess: () => {
                            // Clear any refetch deferred while the form was dirty —
                            // the post-save invalidation will hydrate with the merged
                            // server snapshot, so the queued copy would only flicker
                            // briefly before being overwritten.
                            pendingFingerprintRef.current = null;
                            pendingExpendituresRef.current = null;
                            // Re-baseline so form.isDirty() returns false; otherwise
                            // the Save button stays visible forever after a save.
                            form.resetDirty(form.getValues());
                            // resetDirty doesn't trigger a render in uncontrolled
                            // mode — force one so the Save button re-evaluates and
                            // hides on the very next paint.
                            forceRender();
                            toast.success('Your data has been saved successfully!');
                          },
                          onError: () => {
                            toast.error('There was an error saving your data. Please try again.');
                          },
                        });
                      }
                    }}
                    radius="xl"
                    size="xs"
                    leftSection={<Icon name="check_circle" color={baseWhite} size={16} />}
                  >
                    Save Changes
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      // If a refetch arrived while the user was editing, the queued
                      // snapshot IS the latest server state and supersedes both the
                      // user's edits and the pre-edit form values. Apply that first;
                      // only fall back to form.reset() when nothing was queued.
                      const drained = drainPendingSnapshot();
                      if (!drained) form.reset();
                      // form.reset() is synchronous (see Mantine use-form-values.cjs)
                      // but does NOT notify watch subscribers, and form.setValues()
                      // inside drainPendingSnapshot doesn't either. Recompute every
                      // rollup slot from whichever snapshot is now live.
                      rebuildRollups();
                    }}
                    radius="xl"
                    size="xs"
                    leftSection={<Icon name="cancel" color={inkLight} size={16} />}
                  >
                    Cancel
                  </Button>
                </Group>
              )}
              <TextInput
                placeholder={'Search for Project or Board'}
                value={searchTerm ?? ''}
                onChange={(e) => setTableSearchTerm(e.target.value)}
                leftSection={<Icon name="search" />}
                radius="xl"
                style={{ width: 400 }}
              />
            </Group>
          </Group>
          <Divider />
          <div style={{ display: 'table', tableLayout: 'fixed', width: '100%' }}>
            <ScrollArea w="100%" h="100%" offsetScrollbars type="always" scrollbarSize={10} scrollbars="x">
              <MantineTable verticalSpacing="5" withRowBorders={false}>
                <MantineTable.Thead>
                  <MantineTable.Tr py={24}>
                    <MantineTable.Th style={{ minWidth: 200 }}>
                      <Text fw={700}>{t('financials.table.project_board')}</Text>
                    </MantineTable.Th>
                    {MONTH_LABELS.map((label) => (
                      <MantineTable.Th
                        key={label}
                        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        <Text c={skyDark}>{label}</Text>
                      </MantineTable.Th>
                    ))}
                  </MantineTable.Tr>
                </MantineTable.Thead>
                <MantineTable.Tbody>
                  {selectedYear &&
                    filteredProjects.map((project) => (
                      <ProjectRow
                        key={project.id}
                        projectId={project.id}
                        project={project}
                        selectedYear={selectedYear}
                      />
                    ))}
                </MantineTable.Tbody>
              </MantineTable>
            </ScrollArea>
          </div>
        </Stack>
      </RollupStoreProvider>
    </SpendingFormProvider>
  );
}

/**
 * Computes the sum of `expenditures` rows for a given project + year + month,
 * skipping null/empty budgets. Pure function, used by both the initial rollup
 * computation and the live-update callback fired from `form.watch`.
 *
 * @param {SpendingForm[]} expenditures - All form rows
 * @param {string} projectId - Project to roll up
 * @param {number} selectedYear - Year scope
 * @param {number} month - Month scope (1-12)
 * @returns {number | null} Sum, or null when no cell in this slot has a numeric budget
 */
function computeProjectRollup(
  expenditures: SpendingForm[],
  projectId: string,
  selectedYear: number,
  month: number,
): number | null {
  let total = 0;
  let hasAny = false;
  for (const entry of expenditures) {
    if (entry.project_id !== projectId) continue;
    if (entry.year !== selectedYear) continue;
    if (entry.month !== month) continue;
    if (entry.subproject_id == null) continue;
    if (entry.budget == null) continue;
    if ((entry.budget as unknown) === '') continue;
    hasAny = true;
    total += entry.budget;
  }
  return hasAny ? total : null;
}

/**
 * Live monthly rollup for a single (project, year, month) cell in the summary row.
 *
 * Subscribes to ONE slot in the shared `RollupStore` via `useSyncExternalStore`.
 * The store is updated from two places: (1) the bulk-populate layout effect in
 * ByDate on mount and on data refetch, (2) the cell `onChange` wrapper after a
 * keystroke updates a subproject in this project + month. `useSyncExternalStore`
 * uses `Object.is` to bail out when this cell's slot didn't change, so a
 * keystroke in any OTHER (project, year, month) slot doesn't wake this listener.
 *
 * This component lives outside the memoized `ProjectRow` boundary so re-rendering
 * a rollup cell does not re-render the 12 × subprojects NumberInput grid below it.
 *
 * @param {ProjectRollupCellProps} props
 * @returns {JSX.Element} The rollup number (or empty string when no cells have a value)
 */
function ProjectRollupCell({ projectId, selectedYear, month }: ProjectRollupCellProps) {
  // Subscribes to a single slot in the rollup store via useSyncExternalStore.
  // This component re-renders ONLY when its own (project, year, month) rollup
  // changes; a keystroke in a different project notifies a different slot and
  // does not wake this listener. See https://react.dev/reference/react/useSyncExternalStore.
  const store = useRollupStore();
  const slotKey = rollupKey(projectId, selectedYear, month);
  const subscribe = useCallback((listener: () => void) => store.subscribe(slotKey, listener), [store, slotKey]);
  const getSnapshot = useCallback(() => store.get(slotKey), [store, slotKey]);
  const rollup = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  return (
    <Text fw={700} size="sm">
      {rollup ?? ''}
    </Text>
  );
}

/**
 * One project's worth of rows: a summary row showing monthly rollups across
 * the project's subprojects, plus a detail row per subproject when the
 * disclosure is open. Memoized so that typing into one project's cell does
 * not re-render other projects (rollup cells subscribe individually via
 * `ProjectRollupCell` and update without re-rendering the rest of the row).
 *
 * @param {ProjectRowProps} props - Row props (no `form` — pulled via context)
 * @returns {JSX.Element} The summary row plus optional detail rows
 */
const ProjectRow = memo(function ProjectRow({ projectId, project, selectedYear }: ProjectRowProps) {
  const form = useSpendingFormContext();
  const rollupStore = useRollupStore();
  const [isOpen, { toggle }] = useDisclosure(true);

  // O(1) lookup from cell key → index into form.getValues().expenditures.
  // Computed inline (not memoized): the map must reflect the CURRENT array order,
  // because after a `form.setValues(...)` the order can change (e.g., projects
  // were added). A `useMemo` with stable deps would silently bind inputs to wrong
  // fields. ProjectRow is memoized, so this loop only runs when ProjectRow actually
  // re-renders (mount, isOpen toggle, or parent prop change).
  const expenditureIndexByKey = new Map<string, number>();
  {
    const expenditures = form.getValues().expenditures;
    for (let i = 0; i < expenditures.length; i += 1) {
      expenditureIndexByKey.set(expenditures[i].key, i);
    }
  }

  const sortedSubprojects = useMemo(
    () => [...project.subprojects].sort((a, b) => a.name.localeCompare(b.name)),
    [project.subprojects],
  );

  const rows: ReactElement[] = [
    <MantineTable.Tr key={project.id} py={24}>
      <MantineTable.Th>
        <Flex>
          <Icon
            name={isOpen ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}
            onClick={toggle}
            style={{ cursor: 'pointer' }}
            color="black"
          />
          <Text fw={700}>{project.name}</Text>
        </Flex>
      </MantineTable.Th>
      {MONTH_NAMES.map((monthName, i) => (
        <MantineTable.Th key={monthName}>
          <ProjectRollupCell projectId={projectId} selectedYear={selectedYear} month={i + 1} />
        </MantineTable.Th>
      ))}
    </MantineTable.Tr>,
  ];

  if (isOpen) {
    for (const subproject of sortedSubprojects) {
      rows.push(
        <MantineTable.Tr key={`${projectId}-${subproject.id}`} py={24}>
          <MantineTable.Td pl={48}>{subproject.name}</MantineTable.Td>
          {MONTH_NAMES.map((_, idx) => {
            const month = idx + 1;
            const key = `${projectId}-${subproject.id}-${selectedYear}-${month}`;
            const i = expenditureIndexByKey.get(key);
            if (i === undefined) {
              // Should not happen post-T11 (every cell is pre-populated). Guard for
              // the brief window between mount and the reset effect.
              return <MantineTable.Td key={month} />;
            }
            const inputProps = form.getInputProps(`expenditures.${i}.budget`);
            // Wrap onChange so we (a) let Mantine update its internal form state,
            // then (b) synchronously push the new rollup for THIS (project, year,
            // month) into the rollup store. Only the one ProjectRollupCell
            // subscribed to this slot wakes up — no global notify storm.
            const handleChange = (value: number | string) => {
              inputProps.onChange?.(value);
              const nextRollup = computeProjectRollup(form.getValues().expenditures, projectId, selectedYear, month);
              rollupStore.set(rollupKey(projectId, selectedYear, month), nextRollup);
            };
            return (
              <MantineTable.Td key={month}>
                <NumberInput
                  hideControls
                  style={{ width: 90 }}
                  radius="md"
                  {...inputProps}
                  onChange={handleChange}
                  key={form.key(`expenditures.${i}.budget`)}
                />
              </MantineTable.Td>
            );
          })}
        </MantineTable.Tr>,
      );
    }
  }

  return <>{rows}</>;
});

export {
  ByDate,
  buildInitialExpenditures,
  computeProjectRollup,
  computeSpendingFingerprint,
  MONTH_LABELS,
  MONTH_NAMES,
  ProjectRollupCell,
  ProjectRow,
};
